import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';
import { AiOutlineSend, AiOutlineClose } from 'react-icons/ai';

interface CommentFormProps {
  onSubmit: (content: string) => Promise<void>;
  onCancel?: () => void;
  initialContent?: string;
  placeholder?: string;
  submitLabel?: string;
  className?: string;
}

const formatCharacterCount = (current: number, max: number): string => `${current}/${max} characters`;

const getCharacterCountColor = (current: number, max: number): string => {
  const percentage = (current / max) * 100;
  if (percentage >= 90) return 'text-red-600';
  if (percentage >= 75) return 'text-yellow-600';
  return 'text-gray-500';
};

const CommentForm: React.FC<CommentFormProps> = ({
  onSubmit,
  onCancel,
  initialContent = '',
  placeholder = 'Write a comment...',
  submitLabel = 'Post Comment',
  className = ''
}) => {
  const { isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [content, setContent] = useState(initialContent);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(content.trim());
      setContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${className}`}>
        <p className="text-gray-600 text-center">
          Please <a href="/login" className="text-[#4B3F72] hover:underline font-medium">log in</a> to leave a comment.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <div className="mb-3">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={placeholder}
          className="w-full p-3 bg-gray-100 border border-gray-300 rounded-md focus:ring-[#4B3F72] focus:border-[#4B3F72] resize-none text-gray-700 placeholder-gray-400"
          rows={3}
          required
        />
      </div>
      
      <div className="flex items-center justify-between">
        <div className={`text-sm ${getCharacterCountColor(content.length, 1000)}`}>
          {formatCharacterCount(content.length, 1000)}
        </div>
        
        <div className="flex space-x-2">
          {onCancel && (
            <Button
              variant="secondary"
              size="small"
              onClick={onCancel}
              type="button"
            >
              <AiOutlineClose className="w-3 h-3 mr-1" />
              Cancel
            </Button>
          )}
          
          <Button
            variant="primary"
            size="small"
            type="submit"
            disabled={isSubmitting || !content.trim()}
            isLoading={isSubmitting}
          >
            <AiOutlineSend className="w-3 h-3 mr-1" />
            {submitLabel}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CommentForm;
